import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { motionReduced } from './motion';

export function Spinner() {
  return <span className={`feedback-spinner${motionReduced() ? '' : ' feedback-spinning'}`} aria-hidden="true" />;
}

export function SuccessCheck() {
  return (
    <svg className="feedback-check" viewBox="0 0 20 20" aria-hidden="true">
      <path d="m4 10 4 4 8-9" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

export function Checking() {
  return (
    <span>
      確認中
      <span className="feedback-dots" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
    </span>
  );
}

export function Skeleton({ shape = 'line' }: { shape?: 'line' | 'block' | 'card' }) {
  if (shape === 'card')
    return (
      <span className="skeleton-frame" aria-hidden="true">
        <Skeleton />
        <Skeleton shape="block" />
        <Skeleton />
      </span>
    );
  return <span className={`skeleton skeleton-${shape}`} aria-hidden="true" />;
}

export function LoadingContent({
  loading,
  shape = 'table',
  children,
}: {
  loading: boolean;
  shape?: 'table' | 'cards' | 'overview' | 'settings';
  children: ReactNode;
}) {
  const [visible, setVisible] = useState(loading);
  useEffect(() => {
    if (loading) {
      setVisible(true);
      return;
    }
    const timer = setTimeout(() => setVisible(false), motionReduced() ? 0 : 180);
    return () => clearTimeout(timer);
  }, [loading]);
  return (
    <div className="loading-content">
      {visible && (
        <div
          className={`loading-skeleton loading-${shape}${loading ? '' : ' skeleton-leaving'}`}
          role={loading ? 'status' : undefined}
          aria-label={loading ? '読み込み中' : undefined}
          aria-hidden={!loading}
        >
          {Array.from({ length: shape === 'overview' ? 4 : 3 }, (_, index) => (
            <Skeleton key={index} shape={shape === 'table' ? 'block' : 'card'} />
          ))}
        </div>
      )}
      {!loading && <div className="feedback-content">{children}</div>}
    </div>
  );
}

export function useShake() {
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useCallback(() => {
    if (motionReduced() || !ref.current) return;
    ref.current.classList.remove('feedback-shake');
    void ref.current.offsetWidth;
    const element = ref.current;
    element.classList.add('feedback-shake');
    setTimeout(() => element.classList.remove('feedback-shake'), 300);
  }, []);
  return { ref, trigger };
}

/** React のキーを保って退出まで残す。取得結果の更新で登場アニメーションを再実行しない。 */
function retainMissing<T>(previous: readonly T[], next: readonly T[], keyOf: (item: T) => string | number): T[] {
  const ids = new Set(next.map(keyOf));
  const combined = [...next];
  previous.forEach((item, index) => {
    if (!ids.has(keyOf(item))) combined.splice(Math.min(index, combined.length), 0, item);
  });
  return combined;
}

export function useAnimatedList<T>(items: readonly T[], keyOf: (item: T) => string | number) {
  const [retained, setRetained] = useState<T[]>([...items]);
  const keyRef = useRef(keyOf);
  keyRef.current = keyOf;
  const signature = JSON.stringify(items.map(keyOf));
  const latest = useRef(items);
  latest.current = items;
  useEffect(() => {
    setRetained((previous) => retainMissing(previous, latest.current, keyRef.current));
    const timer = setTimeout(() => setRetained([...latest.current]), motionReduced() ? 0 : 200);
    return () => clearTimeout(timer);
  }, [signature]);
  const current = new Map(items.map((item) => [keyOf(item), item]));
  const combined = retainMissing(retained, items, keyOf);
  const itemRef = useCallback((element: HTMLElement | null) => {
    if (!element) return;
    for (const target of [element, ...element.querySelectorAll<HTMLElement>('td, td *')]) {
      target.style.setProperty('--exit-height', `${target.getBoundingClientRect().height}px`);
    }
  }, []);
  return {
    items: combined,
    className: (item: T) => (current.has(keyOf(item)) ? 'feedback-item' : 'feedback-item feedback-exit'),
    ref: itemRef,
  };
}
